import { useQuery } from '@apollo/client';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { gql } from '__generated__/gql';
import { useNavigate, useParams } from 'react-router-dom';

import FocusLayout from 'layouts/FocusLayout';

import ErrorMessage from 'components/ErrorMessage';

import LoadingIndicator from 'primitives/LoadingIndicator';

import InvestInFundDialogButton from './InvestInFundDialogButton';

const FUND_QUERY = gql(`
  query GetFundInviteForConfirmation($id: ID!) {
      fundInvite(id: $id) {
      id
      status
      minimumInvestmentAmount
      maximumInvestmentAmount
      fund {
        id
        name
        description
        image
        closingDate
      }
    }
  }
`);

export default function FundInviteConfirm() {
  const navigate = useNavigate();
  const { fundInviteLinkId } = useParams<{ fundInviteLinkId: string }>() as {
    fundInviteLinkId: string;
  };

  const { data, loading, error, refetch } = useQuery(FUND_QUERY, {
    variables: { id: fundInviteLinkId },
  });

  if (loading) return <LoadingIndicator />;

  if (error) return <ErrorMessage error={error} refetch={refetch} />;

  const fundInvite = data?.fundInvite;
  const fund = fundInvite?.fund;

  return (
    <FocusLayout>
      <>
        <button type="button" onClick={() => navigate('/invest')}>
          <div className="flex items-center flex-nowrap">
            <ArrowLeftIcon className="h-6 w-6 mr-3" aria-hidden="true" />
          </div>
        </button>
        <div className="divide-y divide-gray-200 space-y-9">
          <div className="my-8">
            <h2 className="text-3xl font-bold text-gray-900">
              Confirm your investment into <span className="text-indigo-600">{fund?.name}</span>
            </h2>
            <h3 className="text-lg font-base text-gray-700 mt-1 max-w-2xl">
              We'll notify the fund manager of your interest in this Fund. Wiring instructions will
              be sent to you once the fund manager has confirmed your investment.
            </h3>
          </div>
        </div>
      </>
      <div className="grid grid-cols-5 gap-x-2">
        <div className="col-span-5 bg-white p-4 rounded-lg">
          <InvestInFundDialogButton
            fundId={fund?.id}
            fundName={fund?.name}
            minimumInvestment={fundInvite?.minimumInvestmentAmount || undefined}
            maximumInvestment={fundInvite?.maximumInvestmentAmount || undefined}
          />
        </div>
      </div>
    </FocusLayout>
  );
}
